import {
  promotions,
  quests,
  accounts,
  domains,
  regions,
  prefectures,
  purchaseChannels,
  stores,
  products,
  categories,
  manufacturers,
  segments,
  regionsWithPrefectures,
  productsWithManufacturer,
  getPromotionById,
  getQuestById,
  getQuestByHash,
  getQuestsByPromotionId,
  getQuestBySurveyId,
  getAccountsByDomainId,
  getPrefecturesByRegionId,
  getQuestTargetExpandedByQuestId,
  getPromotionByHash,
  getProgressNotificationsByPromotionId,
  Promotion,
  Quest,
  Account,
  Domain,
  Region,
  Prefecture,
  PurchaseChannel,
  RegionWithPrefectures,
  Store,
  Product,
  ProductWithManufacturer,
  Category,
  Manufacturer,
  Segment,
  QuestTargetExpanded,
  ProgressNotificationExpanded,
} from "../data";

/**
 * urqlのuseQueryと同じインターフェースを返す型
 * 将来GraphQLに移行する際、フック内部のみ変更すればOK
 */
export type QueryResult<T> = {
  data: T | undefined;
  fetching: boolean;
  error: Error | null;
};

// ======================
// Promotions
// ======================

export type PromotionsQueryData = {
  promotions: Promotion[];
};

export const usePromotions = (): QueryResult<PromotionsQueryData> => {
  return { data: { promotions }, fetching: false, error: null };
};

export type PromotionQueryData = {
  promotion: Promotion | null;
};

export const usePromotion = (
  id: string | undefined,
): QueryResult<PromotionQueryData> => {
  if (!id) return { data: undefined, fetching: false, error: null };
  return {
    data: { promotion: getPromotionById(id) ?? null },
    fetching: false,
    error: null,
  };
};

// ======================
// Quests
// ======================

export type QuestsQueryData = {
  quests: Quest[];
};

export type QuestsOptions = {
  promotionId?: string;
};

export const useQuests = (
  options?: QuestsOptions,
): QueryResult<QuestsQueryData> => {
  const filteredQuests = options?.promotionId
    ? getQuestsByPromotionId(options.promotionId)
    : quests;
  return { data: { quests: filteredQuests }, fetching: false, error: null };
};

export type QuestQueryData = {
  quest: Quest | null;
};

export const useQuest = (
  id: string | undefined,
): QueryResult<QuestQueryData> => {
  if (!id) return { data: undefined, fetching: false, error: null };
  return {
    data: { quest: getQuestById(id) ?? null },
    fetching: false,
    error: null,
  };
};

export type QuestByHashQueryData = {
  quest: Quest | null;
};

export const useQuestByHash = (
  hash: string | undefined,
): QueryResult<QuestByHashQueryData> => {
  if (!hash) return { data: undefined, fetching: false, error: null };
  return {
    data: { quest: getQuestByHash(hash) ?? null },
    fetching: false,
    error: null,
  };
};

export const useQuestBySurveyId = (
  surveyId: string | undefined,
): QueryResult<QuestQueryData> => {
  if (!surveyId) return { data: undefined, fetching: false, error: null };
  return {
    data: { quest: getQuestBySurveyId(surveyId) ?? null },
    fetching: false,
    error: null,
  };
};

// ======================
// Regions & Prefectures
// ======================

export type RegionsQueryData = {
  regions: Region[];
};

export const useRegions = (): QueryResult<RegionsQueryData> => {
  return { data: { regions }, fetching: false, error: null };
};

export type PrefecturesQueryData = {
  prefectures: Prefecture[];
};

export type PrefecturesOptions = {
  regionId?: string;
};

export const usePrefectures = (
  options?: PrefecturesOptions,
): QueryResult<PrefecturesQueryData> => {
  const filteredPrefectures = options?.regionId
    ? getPrefecturesByRegionId(options.regionId)
    : prefectures;
  return {
    data: { prefectures: filteredPrefectures },
    fetching: false,
    error: null,
  };
};

export type RegionsWithPrefecturesQueryData = {
  regions: RegionWithPrefectures[];
};

export const useRegionsWithPrefectures =
  (): QueryResult<RegionsWithPrefecturesQueryData> => {
    return {
      data: { regions: regionsWithPrefectures },
      fetching: false,
      error: null,
    };
  };

// ======================
// Purchase Channels
// ======================

export type PurchaseChannelsQueryData = {
  purchaseChannels: PurchaseChannel[];
};

export const usePurchaseChannels =
  (): QueryResult<PurchaseChannelsQueryData> => {
    return { data: { purchaseChannels }, fetching: false, error: null };
  };

// ======================
// Accounts & Domains
// ======================

export type DomainsQueryData = {
  domains: Domain[];
};

export const useDomains = (): QueryResult<DomainsQueryData> => {
  return { data: { domains }, fetching: false, error: null };
};

export type AccountsQueryData = {
  accounts: Account[];
};

export type AccountsOptions = {
  domainId?: string;
};

export const useAccounts = (
  options?: AccountsOptions,
): QueryResult<AccountsQueryData> => {
  const filteredAccounts = options?.domainId
    ? getAccountsByDomainId(options.domainId)
    : accounts;
  return { data: { accounts: filteredAccounts }, fetching: false, error: null };
};

// ======================
// Stores
// ======================

export type StoresQueryData = {
  stores: Store[];
};

export const useStores = (): QueryResult<StoresQueryData> => {
  return { data: { stores }, fetching: false, error: null };
};

// ======================
// Products
// ======================

export type ProductsQueryData = {
  products: Product[];
};

export const useProducts = (): QueryResult<ProductsQueryData> => {
  return { data: { products }, fetching: false, error: null };
};

export type ProductsWithManufacturerQueryData = {
  products: ProductWithManufacturer[];
};

export const useProductsWithManufacturer =
  (): QueryResult<ProductsWithManufacturerQueryData> => {
    return {
      data: { products: productsWithManufacturer },
      fetching: false,
      error: null,
    };
  };

// ======================
// Categories
// ======================

export type CategoriesQueryData = {
  categories: Category[];
};

export const useCategories = (): QueryResult<CategoriesQueryData> => {
  return { data: { categories }, fetching: false, error: null };
};

// ======================
// Manufacturers
// ======================

export type ManufacturersQueryData = {
  manufacturers: Manufacturer[];
};

export const useManufacturers = (): QueryResult<ManufacturersQueryData> => {
  return { data: { manufacturers }, fetching: false, error: null };
};

// ======================
// Segments
// ======================

export type SegmentsQueryData = {
  segments: Segment[];
};

export const useSegments = (): QueryResult<SegmentsQueryData> => {
  return { data: { segments }, fetching: false, error: null };
};

// ======================
// Quest Targets
// ======================

export type QuestTargetQueryData = {
  questTarget: QuestTargetExpanded | null;
};

export const useQuestTarget = (
  questId: string | undefined,
): QueryResult<QuestTargetQueryData> => {
  if (!questId) return { data: undefined, fetching: false, error: null };
  return {
    data: { questTarget: getQuestTargetExpandedByQuestId(questId) ?? null },
    fetching: false,
    error: null,
  };
};

// ======================
// Progress View (公開用)
// ======================

export type ProgressViewQueryData = {
  promotion: Promotion | null;
  quests: Quest[];
  notifications: ProgressNotificationExpanded[];
};

export const useProgressView = (
  hash: string | undefined,
): QueryResult<ProgressViewQueryData> => {
  if (!hash) return { data: undefined, fetching: false, error: null };
  const promotion = getPromotionByHash(hash) ?? null;
  if (!promotion) {
    return {
      data: { promotion: null, quests: [], notifications: [] },
      fetching: false,
      error: null,
    };
  }
  const questList = getQuestsByPromotionId(promotion.id);
  const notifications = getProgressNotificationsByPromotionId(promotion.id);
  return {
    data: { promotion, quests: questList, notifications },
    fetching: false,
    error: null,
  };
};
